import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2,
  Cable,
  AlertCircle,
  User,
  Check,
  ArrowRight,
  Search,
  History,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  useCableSpools,
  useRecordCableUsage,
  useActiveUsers,
  useTransferOwnership,
} from './hooks/useCable';
import { CableHistorySheet } from './components/CableHistorySheet';
import type { CableSpool } from '@/api/legacy';

export function CableUsagePage() {
  const { clientName: clientNameParam } = useParams<{ clientName?: string }>();
  const navigate = useNavigate();
  const isClientLocked = !!clientNameParam;
  const decodedClientName = clientNameParam
    ? decodeURIComponent(clientNameParam)
    : '';

  // Selection state
  const [selectedSpoolId, setSelectedSpoolId] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [clientName, setClientName] = useState<string>(decodedClientName);

  // Filter state (both enabled by default)
  const [onlyMine, setOnlyMine] = useState(true);
  const [onlyNonEmpty, setOnlyNonEmpty] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Transfer state
  const [transferTo, setTransferTo] = useState<string>('');

  // History sheet state
  const [historySheetOpen, setHistorySheetOpen] = useState(false);
  const [historySpoolId, setHistorySpoolId] = useState<string | null>(null);

  // Queries & mutations
  const { data: spools, isLoading } = useCableSpools();
  const { data: activeUsers } = useActiveUsers();
  const recordUsage = useRecordCableUsage();
  const transferOwnership = useTransferOwnership();

  // Filter spools
  const filteredSpools = useMemo(() => {
    if (!spools) return [];

    const query = searchQuery.toLowerCase().trim();

    return spools.filter((spool) => {
      // Search filter
      if (query) {
        const matchesNumber = spool.name.toLowerCase().includes(query);
        const matchesUser = spool.username.toLowerCase().includes(query);
        if (!matchesNumber && !matchesUser) return false;
      }
      // Only mine filter
      if (onlyMine && !spool.isMyOwn) return false;
      // Only non-empty filter
      if (onlyNonEmpty) {
        const remaining = parseFloat(spool.ostatok);
        if (isNaN(remaining) || remaining <= 0) return false;
      }
      return true;
    });
  }, [spools, onlyMine, onlyNonEmpty, searchQuery]);

  // Sort: by number descending (larger numbers first)
  const sortedSpools = useMemo(() => {
    return [...filteredSpools].sort((a, b) => {
      return parseInt(b.name) - parseInt(a.name);
    });
  }, [filteredSpools]);

  const selectedSpool = spools?.find((s) => s.id === selectedSpoolId);
  const historySpool = spools?.find((s) => s.id === historySpoolId);
  const remaining = selectedSpool ? parseFloat(selectedSpool.ostatok) : 0;

  const handleSubmit = () => {
    const amountNum = parseFloat(amount);
    if (!selectedSpoolId || !amountNum || amountNum <= 0 || !clientName.trim())
      return;

    recordUsage.mutate(
      {
        buhta_id: parseInt(selectedSpoolId),
        amount: amountNum,
        company: clientName.trim(),
      },
      {
        onSuccess: () => {
          setAmount('');
          // Navigate back if we came from client page
          if (isClientLocked) {
            navigate(-1);
          } else {
            setSelectedSpoolId('');
          }
        },
      }
    );
  };

  const handleClaimSpool = () => {
    if (!selectedSpoolId) return;
    transferOwnership.mutate({ buhtaId: parseInt(selectedSpoolId), username: '' });
  };

  const handleTransferSpool = () => {
    if (!selectedSpoolId || !transferTo) return;
    transferOwnership.mutate(
      { buhtaId: parseInt(selectedSpoolId), username: transferTo },
      {
        onSuccess: () => {
          setTransferTo('');
          setSelectedSpoolId('');
        },
      }
    );
  };

  const openHistory = (spoolId: string) => {
    setHistorySpoolId(spoolId);
    setHistorySheetOpen(true);
  };

  const isValid =
    selectedSpoolId &&
    clientName.trim() &&
    amount &&
    parseFloat(amount) > 0 &&
    parseFloat(amount) <= remaining;

  const isPending = recordUsage.isPending || transferOwnership.isPending;

  // Convert activeUsers object to array for dropdown
  const userOptions = useMemo(() => {
    if (!activeUsers) return [];
    return Object.entries(activeUsers).map(([username, displayName]) => ({
      username,
      displayName,
    }));
  }, [activeUsers]);

  return (
    <div className="min-h-screen bg-background">
      <Header title="Расход кабеля" showBack />

      <main className="p-4 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск по номеру или сотруднику..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-9"
          />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={onlyMine}
              onChange={(e) => setOnlyMine(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            Только мои
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={onlyNonEmpty}
              onChange={(e) => setOnlyNonEmpty(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            Непустые
          </label>
        </div>

        {/* Spool list */}
        <div className="space-y-2">
          <Label>Выберите бухту</Label>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : sortedSpools.length === 0 ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground justify-center">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">
                {spools?.length === 0 ? 'Нет доступных бухт' : 'Нет бухт по фильтрам'}
              </span>
            </div>
          ) : (
            <div className="border rounded-md max-h-64 overflow-y-auto">
              {sortedSpools.map((spool) => (
                <SpoolItem
                  key={spool.id}
                  spool={spool}
                  isSelected={spool.id === selectedSpoolId}
                  onSelect={() => setSelectedSpoolId(spool.id)}
                  onShowHistory={() => openHistory(spool.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Selected spool details & ownership */}
        {selectedSpool && (
          <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">#{selectedSpool.name}</div>
                <div className="text-sm text-muted-foreground">
                  Остаток: {selectedSpool.ostatok}м
                </div>
              </div>
              {selectedSpool.isMyOwn ? (
                <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                  Моя
                </span>
              ) : (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {selectedSpool.username}
                </span>
              )}
            </div>

            {/* Ownership controls */}
            {selectedSpool.isMyOwn ? (
              <div className="flex items-center gap-2">
                <Select value={transferTo} onValueChange={setTransferTo}>
                  <SelectTrigger className="flex-1 h-8 text-sm">
                    <SelectValue placeholder="Отдать бухту..." />
                  </SelectTrigger>
                  <SelectContent>
                    {userOptions.map((u) => (
                      <SelectItem key={u.username} value={u.username}>
                        {u.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleTransferSpool}
                  disabled={!transferTo || isPending}
                >
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="w-full"
                onClick={handleClaimSpool}
                disabled={isPending}
              >
                {transferOwnership.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Забрать бухту себе
              </Button>
            )}
          </div>
        )}

        {/* Client name input */}
        {selectedSpoolId && (
          <div className="space-y-2">
            <Label htmlFor="clientName">Клиент</Label>
            {isClientLocked ? (
              <div className="text-sm font-medium p-2 bg-muted rounded-md">
                {clientName}
              </div>
            ) : (
              <Input
                id="clientName"
                placeholder="Введите название клиента..."
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
              />
            )}
          </div>
        )}

        {/* Amount input */}
        {selectedSpoolId && (
          <div className="space-y-2">
            <Label htmlFor="amount">Расход (метров)</Label>
            <Input
              id="amount"
              type="number"
              placeholder="Введите метраж..."
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min={1}
              max={remaining}
            />
            {amount && (
              <div className="text-xs text-muted-foreground">
                Остаток после списания: {remaining - (parseFloat(amount) || 0)}м
              </div>
            )}
            {parseFloat(amount) > remaining && (
              <p className="text-xs text-destructive">
                Нельзя списать больше остатка ({remaining}м)
              </p>
            )}
          </div>
        )}

        {/* Submit button */}
        {selectedSpoolId && (
          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
          >
            {recordUsage.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Cable className="h-4 w-4 mr-2" />
            )}
            Списать кабель
          </Button>
        )}
      </main>

      {/* History Sheet */}
      <CableHistorySheet
        open={historySheetOpen}
        onOpenChange={setHistorySheetOpen}
        spool={historySpool || null}
      />
    </div>
  );
}

// Spool list item component
function SpoolItem({
  spool,
  isSelected,
  onSelect,
  onShowHistory,
}: {
  spool: CableSpool;
  isSelected: boolean;
  onSelect: () => void;
  onShowHistory: () => void;
}) {
  const remaining = parseFloat(spool.ostatok);
  const lastUsage = parseFloat(spool.lastrashod) || 0;
  const isEmpty = remaining <= 0;
  const isLow = remaining > 0 && remaining <= 50;
  const isWarning = remaining > 50 && remaining <= 100;

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-3 border-b last:border-b-0 transition-colors',
        isSelected && 'bg-accent',
        isEmpty && 'opacity-50'
      )}
    >
      {/* Radio indicator - clickable */}
      <button
        type="button"
        onClick={onSelect}
        className="mt-1 flex-shrink-0"
      >
        <div
          className={cn(
            'h-4 w-4 rounded-full border-2 flex items-center justify-center',
            isSelected ? 'border-primary bg-primary' : 'border-muted-foreground'
          )}
        >
          {isSelected && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
        </div>
      </button>

      {/* Spool info - clickable */}
      <button
        type="button"
        onClick={onSelect}
        className="flex-1 min-w-0 text-left"
      >
        <div className="flex items-center gap-2">
          <span className="font-medium">#{spool.name}</span>
          <span
            className={cn(
              'text-sm',
              isEmpty && 'text-muted-foreground',
              isLow && 'text-red-600',
              isWarning && 'text-amber-600'
            )}
          >
            {spool.ostatok}м
          </span>
          {spool.isMyOwn && (
            <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
              Моя
            </span>
          )}
        </div>

        {/* Last usage info */}
        {(lastUsage > 0 || spool.company) && (
          <div className="text-xs text-muted-foreground mt-1">
            {lastUsage > 0 && <span>Посл.: {lastUsage}м</span>}
            {lastUsage > 0 && spool.company && <span> → </span>}
            {spool.company && <span className="truncate">{spool.company}</span>}
          </div>
        )}

        {/* Owner (if not mine) */}
        {!spool.isMyOwn && (
          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
            <User className="h-3 w-3" />
            {spool.username}
          </div>
        )}
      </button>

      {/* History button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onShowHistory();
        }}
        className="p-2 hover:bg-muted rounded-md transition-colors flex-shrink-0"
        title="История расхода"
      >
        <History className="h-4 w-4 text-muted-foreground" />
      </button>
    </div>
  );
}
